import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Loader, Shield, Eye, EyeOff } from 'lucide-react';
import { rolesService } from '../services/rolesService';
import { supabase } from '../services/supabaseService';
import { PublicInvitation } from '../types/roles';

const AcceptInvitationPage: React.FC = () => {
  const [invitation, setInvitation] = useState<PublicInvitation | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadInvitation();
  }, []);

  // Clear any stale session before navigating away. accept_invitation creates a
  // new auth user via admin.createUser (no session returned), so any session
  // still in localStorage belongs to a *different* identity and would keep the
  // user logged in as that person if we just did window.location.href = '/'.
  const goHomeCleanly = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Ignore — worst case they end up on the login form with a stale session
      // that authService will handle on the next tick.
    }
    window.location.href = '/';
  };

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(window.location.search);
      const path = window.location.pathname;
      const pathToken = path.startsWith('/accept-invitation/')
        ? path.slice('/accept-invitation/'.length).split(/[/?#]/)[0]
        : null;
      const tokenParam = params.get('token') || pathToken;

      if (!tokenParam) {
        setError('Token de invitación no válido o ausente');
        return;
      }

      // If a session for a *different* user is active in this browser, wipe it
      // so we don't cross identities during the accept flow (the invitee is a
      // fresh user, not the currently logged-in one).
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      }

      const invitationData = await rolesService.getPublicInvitationByToken(tokenParam);

      if (!invitationData) {
        setError('Invitación no encontrada');
        return;
      }

      if (invitationData.status !== 'pending') {
        if (invitationData.status === 'accepted') {
          setError('Esta invitación ya ha sido aceptada');
        } else if (invitationData.status === 'expired') {
          setError('Esta invitación ha expirado');
        } else if (invitationData.status === 'cancelled') {
          setError('Esta invitación ha sido cancelada');
        }
        return;
      }

      const expiresAt = new Date(invitationData.expires_at);
      if (expiresAt < new Date()) {
        setError('Esta invitación ha expirado');
        return;
      }

      setInvitation(invitationData);
      setToken(tokenParam);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Error al cargar la invitación');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!/[A-Z]/.test(password)) {
      return 'La contraseña debe contener al menos una letra mayúscula';
    }
    if (!/[a-z]/.test(password)) {
      return 'La contraseña debe contener al menos una letra minúscula';
    }
    if (!/[0-9]/.test(password)) {
      return 'La contraseña debe contener al menos un número';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Por favor ingresa tu nombre completo');
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!invitation || !token) {
      setError('No se encontró la invitación');
      return;
    }

    try {
      setSubmitting(true);

      const result = await rolesService.acceptInvitation(
        token,
        formData.password,
        formData.name
      );

      if (!result.success) {
        setError(result.error || 'Error al aceptar la invitación');
        return;
      }

      setSuccess(true);

      setTimeout(() => {
        goHomeCleanly();
      }, 3000);
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Error al aceptar la invitación. Por favor intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex flex-col items-center space-y-4">
            <Loader className="w-12 h-12 animate-spin text-blue-600" />
            <p className="text-gray-600">Cargando invitación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Invitación no válida</h2>
            <p className="text-gray-600 text-center">{error}</p>
            <button
              onClick={goHomeCleanly}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ir a Inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">¡Cuenta activada!</h2>
            <p className="text-gray-600 text-center">
              Tu cuenta ha sido creada exitosamente. Redirigiendo al inicio de sesión...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Activa tu cuenta
          </h1>
          <p className="text-gray-600">
            Has sido invitado a unirte al CRM Cero Ideas
          </p>
        </div>

        {invitation && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              <strong>Email:</strong> {invitation.email}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Juan Pérez"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                placeholder="••••••••"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Mínimo 8 caracteres, con mayúsculas, minúsculas y números
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar contraseña *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                placeholder="••••••••"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {submitting && <Loader className="w-5 h-5 animate-spin" />}
            <span>{submitting ? 'Activando cuenta...' : 'Activar cuenta'}</span>
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            ¿Problemas con tu invitación?{' '}
            <a href="mailto:soporte@ceroideas.es" className="text-blue-600 hover:text-blue-800">
              Contacta soporte
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
